import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { MuiThemeProvider } from '@material-ui/core/styles';
import { buildTheme, getThemeCssVars } from 'GlobalStyles';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import { FeatureFlags } from 'UI/constants/featureFlags';
import {
  getDarkSchemeMediaQuery,
  isThemeMode,
  readStoredThemeMode,
  resolveThemeMode,
  storeThemeMode
} from 'UI/theme/themeMode';

const ThemeModeContext = React.createContext(null);

const cssVarsByMode = {};
const getCssVars = mode => {
  cssVarsByMode[mode] = cssVarsByMode[mode] ?? getThemeCssVars(buildTheme(mode), mode);
  return cssVarsByMode[mode];
};

const usePrefersDarkScheme = () => {
  const [prefersDark, setPrefersDark] = useState(() => !!getDarkSchemeMediaQuery()?.matches);

  useEffect(() => {
    const mediaQuery = getDarkSchemeMediaQuery();
    if (!mediaQuery) return undefined;

    const handleChange = event => setPrefersDark(event.matches);
    setPrefersDark(mediaQuery.matches);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  return prefersDark;
};

/**
 * Owns the user's theme choice (light / dark / system), persists it, and provides the matching MUI
 * theme. It also writes the CSS variables behind the static `THEME` and `colors` exports, so static
 * usages switch live together with `makeStyles(theme => ...)` styles.
 */
export const ThemeModeProvider = ({ children }) => {
  const { checkIfFeatureFlagEnabled } = useFeatureFlags();
  const isEnabled = checkIfFeatureFlagEnabled(FeatureFlags.DarkMode);
  const [mode, setModeState] = useState(readStoredThemeMode);
  const prefersDark = usePrefersDarkScheme();

  const resolvedMode = resolveThemeMode({ mode, prefersDark, isEnabled });
  const theme = useMemo(() => buildTheme(resolvedMode), [resolvedMode]);
  const cssVars = getCssVars(resolvedMode);

  useLayoutEffect(() => {
    const root = document.documentElement;
    Object.entries(cssVars).forEach(([name, value]) => root.style.setProperty(name, value));
    root.setAttribute('data-theme', resolvedMode);
  }, [cssVars, resolvedMode]);

  const setMode = useCallback(nextMode => {
    if (!isThemeMode(nextMode)) return;
    storeThemeMode(nextMode);
    setModeState(nextMode);
  }, []);

  const context = useMemo(
    () => ({ isEnabled, mode, resolvedMode, cssVars, setMode }),
    [isEnabled, mode, resolvedMode, cssVars, setMode]
  );

  return (
    <ThemeModeContext.Provider value={context}>
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </ThemeModeContext.Provider>
  );
};

/**
 * @returns {{
 *   isEnabled: boolean,
 *   mode: 'light'|'dark'|'system',
 *   resolvedMode: 'light'|'dark',
 *   cssVars: Object<string, string>,
 *   setMode: (mode: 'light'|'dark'|'system') => void
 * }}
 */
export const useThemeMode = () => {
  const context = React.useContext(ThemeModeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within a ThemeModeProvider');
  }
  return context;
};

/**
 * Painted mode and its CSS variable map, for components that must read concrete colors
 * (charts, canvas). Falls back to light outside a `ThemeModeProvider` (isolated tests, stories).
 * @returns {{ resolvedMode: 'light'|'dark', cssVars: Object<string, string> }}
 */
export const useResolvedTheme = () => {
  const context = React.useContext(ThemeModeContext);
  const resolvedMode = context?.resolvedMode ?? 'light';
  return { resolvedMode, cssVars: context?.cssVars ?? getCssVars(resolvedMode) };
};

export default ThemeModeProvider;
